import React from 'react'
import TransitionGroup from 'react-transition-group/TransitionGroup'

import { NavItem } from './NavItem'
import { SmartListNavigation } from './SmartListNavigation'
import { ObjectViewNavigation } from './ObjectViewNavigation'
import { useAppAction, useAppSelect } from 'src/hooks/useApp'
import { useSearchQuery } from 'src/hooks/useSearchQuery'
import { ISchemaInfo } from 'src/hooks/useStorageObjects'
import { IUseRouterObjectTableSearchQuery } from 'src/hooks/router/useRouterObjectTable'
import SchemesAdminListNavigation from './SchemesLIstNavigation/SchemesAdminListNavigation'

import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import Collapse from '@mui/material/Collapse'
import Divider from '@mui/material/Divider'

import { useTranslation } from 'react-i18next'
import useResponsive from 'src/hooks/useResponsive'
import UserIcon from '../../UserIcon'
import { AdminSvgIcon, TemplateIcon } from 'src/components/icons'
import { PermissionPath, usePermission } from 'src/hooks/usePermission'

interface IGetDefaultView {
  objectsViewType: IUseRouterObjectTableSearchQuery['objectsViewType']
  objectsViewId: IUseRouterObjectTableSearchQuery['objectsViewId']
}

const NavItems = () => {
  const { t } = useTranslation('nav')
  const schemas = useAppSelect.schemas()
  const { isMobile } = useResponsive()

  const schemasCursor = useAppSelect.schemasCursor()
  const loadMoreSchemesAction = useAppAction.loadMoreSchemas()
  const getSchemasActionStatus = useAppAction.getSchemas.status()
  const loadMoreSchemesActionStatus = useAppAction.loadMoreSchemas.status()

  const handleFetchMoreSchemes = () => {
    if (schemasCursor) {
      loadMoreSchemesAction({
        cursor: schemasCursor,
        perPage: 10,
      })
    }
  }

  const showFetchMoreButton = schemasCursor && !getSchemasActionStatus.isPending

  const getDefaultView = (scheme: ISchemaInfo): IGetDefaultView => {
    if (scheme.frontDefaultView) {
      const [objectsViewType, objectsViewId] =
        scheme.frontDefaultView.split('.')

      return {
        objectsViewType: objectsViewType as IGetDefaultView['objectsViewType'],
        objectsViewId,
      }
    }

    return {
      objectsViewType: 'table',
      objectsViewId: undefined,
    }
  }

  return (
    <Stack
      direction="column"
      spacing={3}
      sx={{ fontSize: isMobile ? '1rem' : undefined }}
    >
      {schemas.length > 0 && (
        <Stack direction={'column'}>
          <Typography
            pl={6.2}
            mb={2}
            fontSize={12}
            className="opacity"
            fontWeight={300}
            color="text.secondary"
          >
            {t('management')}
          </Typography>
          <TransitionGroup>
            {schemas
              ?.filter((item) => item.showInMenu)
              .map((scheme) => {
                const { objectsViewType, objectsViewId } =
                  getDefaultView(scheme)

                return (
                  <Collapse key={scheme.identifier}>
                    <SchemeNavItem
                      scheme={scheme}
                      objectsViewType={objectsViewType}
                      objectsViewId={objectsViewId}
                    />
                  </Collapse>
                )
              })}
          </TransitionGroup>

          {showFetchMoreButton && (
            <Box className="opacity" pl={3.8}>
              <Button
                disabled={loadMoreSchemesActionStatus.isPending}
                onClick={handleFetchMoreSchemes}
                size={isMobile ? 'medium' : 'small'}
                sx={{ display: 'inline-block' }}
              >
                <Typography noWrap color="inherit">
                  {t('showMore', { ns: 'common' })}
                </Typography>
              </Button>
            </Box>
          )}
        </Stack>
      )}
      {/* <NavItem
        title={'Tasks'}
        path={`/tasks`}
        icon={'tabler-list-details'}
        permission="tasks"
      /> */}

      <Stack direction={'column'}>
        <NavListTitle title={t('communication')} permission={[]} />

        <NavItem
          title={t('mail')}
          path={`/email`}
          icon={
            <UserIcon
              color="inherit"
              fontSize={24}
              icon="material-symbols-light:stacked-email-outline"
            />
          }
          // permission={'mail'}
        />

        <NavItem
          title={t('templates')}
          icon={<TemplateIcon fill="inherit" fontSize={18} />}
          // permission="templates"
        >
          <NavItem
            title={t('email')}
            path={'/templates/email'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={12}
                icon="pepicons-pencil:circle-filled"
              />
            }
            deep={1}
            // permission="templates"
          />
          <NavItem
            title={t('messages')}
            path={'/templates/message'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={12}
                icon="pepicons-pencil:circle-filled"
              />
            }
            deep={1}
            // permission="templates"
          />
          <NavItem
            title={t('docs')}
            path={'/templates/document'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={12}
                icon="pepicons-pencil:circle-filled"
              />
            }
            deep={1}
            // permission="templates"
          />
        </NavItem>

        <NavItem
          title={t('marketing')}
          path={`/marketing`}
          icon={
            <UserIcon
              color="inherit"
              fontSize={24}
              icon="material-symbols-light:add-notes-outline"
            />
          }
        />
      </Stack>

      <Stack direction={'column'}>
        <NavListTitle
          title={t('settings')}
          permission={['auth.users', 'storage.objectScheme', 'auth.groups']}
        />

        <NavItem
          title={t('administration')}
          icon={<AdminSvgIcon fill="inherit" fontSize={19} />}
          permission={['auth.users', 'storage.objectScheme', 'auth.groups']}
        >
          <NavItem
            title={t('users')}
            path={'/admin/users'}
            icon={
              <UserIcon color="inherit" fontSize={24} icon="tabler-user-plus" />
            }
            deep={1}
            permission={'auth.users'}
          />
          <NavItem
            title={t('userGroup')}
            path={'/admin/users-groups'}
            icon={
              <UserIcon color="inherit" fontSize={24} icon="mdi:folder-user" />
            }
            deep={1}
            permission={'auth.groups'}
          />
          <NavItem
            title={t('apiKeys')}
            path={'/admin/api-keys'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={24}
                icon="tabler:circle-key"
              />
            }
            deep={1}
            permission={'auth.users'}
          />

          <NavItem
            title={t('integration')}
            path={'/admin/integration'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={24}
                icon="material-symbols-light:settings-input-component-sharp"
              />
            }
            deep={1}
            permission={'auth.users'}
          />

          <NavItem
            title={t('schemes')}
            path={'/admin/schemes'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={24}
                icon="material-symbols-light:table-edit"
              />
            }
            deep={1}
            permission={'storage.objectScheme'}
            children={<SchemesAdminListNavigation />}
          />
        </NavItem>

        <NavItem
          title={t('numbers')}
          icon={
            <UserIcon
              color="inherit"
              fontSize={24}
              icon="material-symbols:settings"
            />
          }
          // permission={'auth.users'}
        >
          <NavItem
            title={t('numbers')}
            path={'/numbers'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={24}
                icon="tabler-phone-plus"
              />
            }
            deep={1}
            // permission={'auth.users'}
          />
          <NavItem
            title={t('callHistory')}
            path={'/callHistory'}
            icon={
              <UserIcon
                color="inherit"
                fontSize={24}
                icon="tabler-phone-plus"
              />
            }
            deep={1}
            permission={'auth.users'}
          />
        </NavItem>

        <NavItem
          title={t('billingHistory')}
          path={`/billing-history`}
          icon={
            <UserIcon color="inherit" fontSize={24} icon="stash:billing-info" />
          }
          permission={'auth.partner.billingStat'}
        />
        <NavItem
          title={t('forms')}
          path={`/forms`}
          icon={
            <UserIcon
              color="inherit"
              fontSize={24}
              icon="fluent:form-multiple-48-regular"
            />
          }
          permission={'auth.users'}
        />
      </Stack>

      {/* <NavItem
        title={'Playbooks'}
        path={`/playbooks`}
        icon={
          <UserIcon color="inherit" fontSize={24} icon='tabler-tournament' />}
        permission={'auth.users'}
      /> */}

      {/* <NavItem
        title={'TG Accounts'}
        path={'/tg-accounts'}
        icon={
          <UserIcon
            color="inherit"
            fontSize={24}
            icon="tabler-brand-telegram"
          />
        }
        // permission={'communication.accounts'}
        deep={0}
      /> */}
    </Stack>
  )
}

interface ISchemeNavItem {
  scheme: ISchemaInfo
  objectsViewType: IUseRouterObjectTableSearchQuery['objectsViewType']
  objectsViewId: IUseRouterObjectTableSearchQuery['objectsViewId']
}

const SchemeNavItem = ({
  scheme,
  objectsViewType,
  objectsViewId,
}: ISchemeNavItem) => {
  const { setPathQuery } = useSearchQuery<IUseRouterObjectTableSearchQuery>()

  const hasContent = scheme.smartListsCount > 0 || scheme.stageFieldsCount > 0

  const renderContent = React.useCallback(() => {
    return <NavItemContent scheme={scheme} />
  }, [scheme])

  return (
    <NavItem
      title={scheme.title}
      key={scheme.identifier}
      path={`storage/${scheme.identifier}`}
      onClick={() => {
        setPathQuery(
          `storage/${scheme.identifier}`,
          {
            objectsViewType,
            objectsViewId,
          },
          false,
          false
        )
      }}
      icon={
        <UserIcon
          color="inherit"
          fontSize={20}
          icon={scheme.icon || 'material-symbols-light:table-chart'}
        />
      }
      content={hasContent ? renderContent : undefined}
    />
  )
}

interface INavItemContentProps {
  scheme: ISchemaInfo
}
const NavItemContent = ({ scheme }: INavItemContentProps) => {
  return (
    <Stack mt={2} px={3.5} onClick={(event) => event.stopPropagation()}>
      <ObjectViewNavigation scheme={scheme} />
      <Divider sx={{ borderColor: 'grey.800', my: 0, ml: 5, mr: 8 }} />
      <SmartListNavigation scheme={scheme} />
    </Stack>
  )
}

interface NavListHeaderProps {
  title: string
  permission: PermissionPath
}

const NavListTitle = (props: NavListHeaderProps) => {
  const { title, permission } = props
  const isAccess = usePermission(permission)

  if (permission.length > 0 && !isAccess) return <></>

  return (
    <Typography
      mb={2}
      pl={6.2}
      fontSize={12}
      className="opacity"
      fontWeight={300}
      color="text.secondary"
    >
      {title}
    </Typography>
  )
}
export function SchemeListSkeleton(props: { count?: number }) {
  const { count = 2 } = props

  return (
    <>
      {new Array(count).fill(null).map((_, index) => (
        <Stack direction="row" py={3} key={index}>
          <Stack width={47} direction="row" justifyContent="center">
            <Skeleton
              variant="circular"
              sx={{
                width: 24,
                height: 24,
                backgroundColor: 'grey.500',
              }}
            />
          </Stack>

          <Skeleton
            variant="text"
            sx={{ width: 100, height: 18, backgroundColor: 'grey.500' }}
          />
        </Stack>
      ))}
    </>
  )
}

export default NavItems
